import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import apiClient from '../../http-common';
import CryptoList from '../CryptoList';
import {
  accountBalance,
  referralBalance,
  bonusBalance,
  currentEarning,
  currentInvestment,
} from '../Request/Wallets';

/**
 * Home dashboard — restructured to match the "futuristic" Clanelevation layout.
 *
 * Data sources (all from the existing Laravel API):
 *   GET /api/balance    -> AnalysisController@wallets
 *   GET /api/histories  -> AnalysisController@histories
 *
 * Theme:
 *   Reads localStorage['Elliptic Managements-Theme']  ('dark-mode' | anything else)
 *   Listens to the global 'themeChanged' window event (already dispatched by
 *   SideBar.js / SidebarLarge.js when the user toggles dark mode).
 */
const Index = () => {
  // ---------- user / role ----------
  const accountType = localStorage.getItem('user-type');
  let user = localStorage.getItem('delox-user');
  if (user != null) {
    try {
      user = JSON.parse(user);
    } catch (e) {
      user = {};
    }
  } else {
    user = {};
  }

  // ---------- theme ----------
  const readTheme = () => {
    const stored = localStorage.getItem('Elliptic Managements-Theme');
    return stored === 'dark-mode' ? 'dark' : 'light';
  };
  const [theme, setTheme] = useState(readTheme);
  const isDark = theme === 'dark';

  // ---------- state ----------
  const [, setTick] = useState(0); // forces re-read of localStorage helpers
  const [histories, setHistories] = useState(() => {
    const stored = localStorage.getItem('binaryfair-account-histories');
    return stored ? JSON.parse(stored) : null;
  });

  // ---------- /api/balance ----------
  const { refetch: getWallets } = useQuery(
    'wallet-balances',
    async () => apiClient.get('/api/balance'),
    {
      enabled: true,
      onSuccess: (res) => {
        if (res.data?.status === 'success') {
          const balanceSheet = {
            ref_bal: res.data.ref_bal,
            account_balance: res.data.account_balance,
            per_seconds_earning: res.data.per_seconds_earning,
            current_earning: res.data.current_earning,
            bonus_balanace: res.data.bonus_balanace,
            current_investment: res.data.current_investment,
          };
          localStorage.setItem(
            'binaryfair-wallet-balances',
            JSON.stringify(balanceSheet)
          );
          setTick((n) => n + 1);
        }
      },
    }
  );

  // ---------- /api/histories ----------
  const { refetch: getHistories } = useQuery(
    'account-histories',
    async () => apiClient.get('/api/histories'),
    {
      enabled: true,
      onSuccess: (res) => {
        if (res.data?.status === 'success') {
          const accountHistories = {
            withdrawals: res.data.withdrawals,
            investments: res.data.investments,
            fundings: res.data.fundings,
            referrals: res.data.referrals,
            plans: res.data.plans,
            wallets: res.data.wallets,
            toasts: res.data.toasts,
            investmentHistory: res.data.investmentHistory,
            cryptowallets: res.data.cryptowallets,
          };
          localStorage.setItem(
            'binaryfair-account-histories',
            JSON.stringify(accountHistories)
          );
          setHistories(accountHistories);
        }
      },
    }
  );

  // ---------- effects ----------
  useEffect(() => {
    document.title = 'DASHBOARD';
    if (window && window.init_template) {
      window.init_template();
    }
    getWallets();
    getHistories();

    // Sync with the rest of the app's theme toggle
    const onThemeChange = () => setTheme(readTheme());
    window.addEventListener('themeChanged', onThemeChange);

    // Also sync if another tab toggles the theme
    const onStorage = (e) => {
      if (e.key === 'Elliptic Managements-Theme') setTheme(readTheme());
    };
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('themeChanged', onThemeChange);
      window.removeEventListener('storage', onStorage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- derived values ----------
  const fmt = (val) => {
    const n = Number(val || 0);
    return n.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const totalDeposit = (() => {
    if (!histories) return 0;
    const approvedInvest = (histories.investments || [])
      .filter((i) => i.status === 'approved' || i.status === 'completed')
      .reduce((sum, i) => sum + parseFloat(i.amount || 0), 0);
    const approvedFunding = (histories.fundings || [])
      .filter((f) => f.status === 'approved')
      .reduce((sum, f) => sum + parseFloat(f.amount || 0), 0);
    return approvedInvest + approvedFunding;
  })();

  const totalWithdrawal = (() => {
    if (!histories) return 0;
    return (histories.withdrawals || [])
      .filter((w) => w.status === 'approved')
      .reduce((sum, w) => sum + parseFloat(w.amount || 0), 0);
  })();

  const activePlans = (() => {
    if (!histories) return [];
    return (histories.investments || []).filter(
      (i) => i.status === 'approved'
    );
  })();

  const recentTransactions = (() => {
    if (!histories) return [];
    const txs = [];
    (histories.investments || []).forEach((i) =>
      txs.push({
        id: `inv-${i.id}`,
        date: i.created_at,
        type: 'Investment',
        amount: i.amount,
        status: i.status,
      })
    );
    (histories.withdrawals || []).forEach((w) =>
      txs.push({
        id: `wd-${w.id}`,
        date: w.created_at,
        type: 'Withdrawal',
        amount: w.amount,
        status: w.status,
      })
    );
    (histories.fundings || []).forEach((f) =>
      txs.push({
        id: `fd-${f.id}`,
        date: f.created_at,
        type: 'Deposit',
        amount: f.amount,
        status: f.status,
      })
    );
    return txs
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);
  })();

  // referral link
  const referralCode = user?.referral_code || user?.username || user?.name || '';
  const referralLink = `${window.location.origin}/invitation/${encodeURIComponent(
    referralCode
  )}`;

  const copyReferral = () => {
    if (!navigator.clipboard) {
      alert(
        'Copying to clipboard only works on secure sites viewed through a modern browser.'
      );
      return;
    }
    navigator.clipboard.writeText(referralLink).then(() => {
      alert('Referral link copied to clipboard');
    });
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // ---------- admin branch (unchanged behaviour) ----------
  if (accountType === 'admin') {
    return (
      <div className="container px-sm-4 px-0" style={{ marginTop: '40px' }}>
        <div className="card card-style overflow-visible mt-5">
          <div className="mt-5"></div>
          {user?.profile && (
            <img
              style={{ width: '180px', height: '180px' }}
              src={user.profile}
              alt="profile"
              className="mx-auto rounded-circle mt-n5 shadow-l"
            />
          )}
          <h1 className="color-theme text-center font-30 pt-0 pb-2 mb-0">
            Welcome Admin {user?.name}
          </h1>
        </div>
        <div className="text-center">
          <span className="text-success font-16">
            kindly use the navigation menu to access all admin functions, thank you.
          </span>
        </div>

        {/* Live crypto prices */}
        <CryptoList />
      </div>
    );
  }

  // ---------- user dashboard (futuristic layout, theme-aware) ----------
  return (
    <div className={`dash-root ${isDark ? 'theme-dark' : 'theme-light'}`}>
      <style>{`
        /* ---------- theme tokens ---------- */
        .dash-root.theme-dark {
          --primary-bg: #0a0a0a;
          --card-bg: rgba(30, 30, 30, 0.7);
          --card-border: rgba(255, 255, 255, 0.05);
          --card-border-hover: rgba(0, 242, 254, 0.3);
          --accent-color: #00f5ff;
          --accent-gradient: linear-gradient(135deg, #00f2fe 0%, #00f5ff 100%);
          --accent-glow: rgba(0, 242, 254, 0.2);
          --accent-soft: rgba(0, 242, 254, 0.1);
          --text-primary: #ffffff;
          --text-secondary: #b0b0b0;
          --glass-effect: rgba(255, 255, 255, 0.05);
          --divider: rgba(255, 255, 255, 0.1);
          --input-bg: rgba(0, 0, 0, 0.3);
          --input-border: rgba(255, 255, 255, 0.1);
          --th-bg: rgba(0, 242, 254, 0.1);
          --success-color: #00e676;
          --danger-color: #ff3d71;
          --warning-color: #ffaa00;
          --copy-btn-text: #000;
          --empty-btn-text: #000;
        }

        .dash-root.theme-light {
          --primary-bg: #f5f7fb;
          --card-bg: #ffffff;
          --card-border: rgba(15, 23, 42, 0.08);
          --card-border-hover: rgba(0, 153, 178, 0.35);
          --accent-color: #0095b6;
          --accent-gradient: linear-gradient(135deg, #00b8d4 0%, #0095b6 100%);
          --accent-glow: rgba(0, 149, 182, 0.18);
          --accent-soft: rgba(0, 149, 182, 0.08);
          --text-primary: #0f172a;
          --text-secondary: #475569;
          --glass-effect: rgba(15, 23, 42, 0.04);
          --divider: rgba(15, 23, 42, 0.08);
          --input-bg: #f8fafc;
          --input-border: rgba(15, 23, 42, 0.12);
          --th-bg: rgba(0, 149, 182, 0.08);
          --success-color: #16a34a;
          --danger-color: #dc2626;
          --warning-color: #d97706;
          --copy-btn-text: #ffffff;
          --empty-btn-text: #ffffff;
        }

        .dash-root {
          color: var(--text-primary);
          font-family: 'Poppins', 'Inter', sans-serif;
          padding: 1.5rem;
          min-height: 100vh;
          background: var(--primary-bg);
          transition: background-color 0.3s ease, color 0.3s ease;
        }
        .dash-root * { box-sizing: border-box; }

        /* ---------- header ---------- */
        .dash-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 0;
          border-bottom: 1px solid var(--divider);
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .dash-greeting h5 {
          font-size: 1.5rem;
          font-weight: 500;
          margin: 0;
          background: var(--accent-gradient);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .dash-actions .dash-btn {
          border-radius: 8px;
          font-weight: 500;
          padding: 0.75rem 1.5rem;
          margin-left: 0.75rem;
          transition: all 0.3s ease;
          border: none;
          color: #fff;
          text-decoration: none;
          display: inline-block;
          cursor: pointer;
          box-shadow: 0 4px 15px var(--accent-glow);
        }
        .dash-actions .dash-btn-success { background: var(--success-color); }
        .dash-actions .dash-btn-danger  { background: var(--danger-color); }
        .dash-actions .dash-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px var(--accent-glow);
        }

        /* ---------- alert ---------- */
        .dash-alert {
          border-radius: 10px;
          border: none;
          background: var(--glass-effect);
          color: var(--text-primary);
          border-left: 4px solid var(--accent-color);
          padding: 1rem 1.25rem;
          margin-bottom: 2rem;
          backdrop-filter: blur(10px);
        }

        /* ---------- stats grid ---------- */
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }
        .stat-card {
          background: var(--card-bg);
          border-radius: 16px;
          padding: 1.5rem;
          backdrop-filter: blur(10px);
          border: 1px solid var(--card-border);
          transition: all 0.3s ease;
          position: relative;
          overflow: hidden;
        }
        .stat-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px var(--accent-glow);
          border-color: var(--card-border-hover);
        }
        .stat-card::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 70% 20%, var(--accent-soft) 0%, transparent 70%);
          pointer-events: none;
        }
        .stat-icon {
          width: 50px; height: 50px;
          border-radius: 12px;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 1rem;
          background: var(--accent-soft);
          color: var(--accent-color);
          font-size: 1.5rem;
        }
        .stat-value {
          font-size: 1.5rem; font-weight: 600; margin: 0.5rem 0;
          background: var(--accent-gradient);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }
        .stat-label { color: var(--text-secondary); font-size: 0.9rem; margin: 0; }

        /* ---------- sections ---------- */
        .section-card {
          background: var(--card-bg);
          border-radius: 16px;
          padding: 1.5rem;
          margin-bottom: 2rem;
          backdrop-filter: blur(10px);
          border: 1px solid var(--card-border);
        }
        .section-title {
          font-size: 1.25rem;
          font-weight: 500;
          margin-bottom: 1.5rem;
          color: var(--accent-color);
          display: flex; align-items: center;
        }
        .section-title i { margin-right: 0.75rem; }

        /* ---------- plans ---------- */
        .plan-list {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1rem;
        }
        .plan-item {
          background: var(--card-bg);
          border-radius: 12px;
          padding: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          transition: all 0.3s ease;
          border: 1px solid var(--card-border);
          text-decoration: none;
          color: inherit;
        }
        .plan-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 8px 25px var(--accent-glow);
          border-color: var(--card-border-hover);
        }
        .plan-info h6 { font-size: 1rem; margin: 0; font-weight: 500; color: var(--text-primary); }
        .plan-info p  { color: var(--text-secondary); font-size: 0.9rem; margin: 0.25rem 0 0; }
        .plan-arrow   { color: var(--accent-color); font-size: 1.25rem; }
        .empty-state  { text-align: center; padding: 2rem 1rem; color: var(--text-secondary); }
        .empty-state .dash-btn {
          margin-top: 0.75rem;
          background: var(--accent-gradient);
          color: var(--empty-btn-text);
          font-weight: 600;
          padding: 0.6rem 1.25rem;
          border-radius: 8px;
          border: none;
          text-decoration: none;
          display: inline-block;
        }

        /* ---------- transactions ---------- */
        .tx-wrap { overflow-x: auto; }
        .tx-table { width: 100%; border-collapse: separate; border-spacing: 0 0.75rem; }
        .tx-table thead th {
          background: var(--th-bg);
          color: var(--accent-color);
          font-weight: 500;
          padding: 1rem;
          text-align: left;
          border: none;
        }
        .tx-table tbody tr {
          background: var(--card-bg);
          transition: all 0.3s ease;
        }
        .tx-table tbody tr:hover {
          transform: translateX(5px);
          box-shadow: 0 5px 15px var(--accent-glow);
        }
        .tx-table td {
          padding: 1rem;
          border-top: 1px solid var(--card-border);
          border-bottom: 1px solid var(--card-border);
          color: var(--text-primary);
        }
        .tx-table td:first-child {
          border-left: 1px solid var(--card-border);
          border-radius: 8px 0 0 8px;
        }
        .tx-table td:last-child {
          border-right: 1px solid var(--card-border);
          border-radius: 0 8px 8px 0;
        }
        .tx-status {
          padding: 0.3rem 0.6rem;
          border-radius: 6px;
          font-size: 0.8rem;
          text-transform: capitalize;
          display: inline-block;
        }
        .tx-status.approved  { background: rgba(34,197,94,0.12);  color: var(--success-color); }
        .tx-status.pending   { background: rgba(217,119,6,0.12);  color: var(--warning-color); }
        .tx-status.declined  { background: rgba(220,38,38,0.12);  color: var(--danger-color); }
        .tx-status.completed { background: var(--accent-soft);    color: var(--accent-color); }

        /* ---------- referral ---------- */
        .referral-card {
          background: var(--card-bg);
          border-radius: 16px;
          padding: 1.5rem;
          backdrop-filter: blur(10px);
          border: 1px solid var(--card-border);
          position: relative;
          overflow: hidden;
        }
        .referral-title { font-size: 1.25rem; margin-bottom: 0.5rem; color: var(--accent-color); }
        .referral-input-row { display: flex; gap: 0.5rem; margin-top: 1rem; }
        .referral-input {
          flex: 1;
          background: var(--input-bg);
          border: 1px solid var(--input-border);
          color: var(--text-primary);
          border-radius: 8px;
          padding: 0.75rem 1rem;
          outline: none;
        }
        .referral-input:focus {
          box-shadow: 0 0 0 0.2rem var(--accent-glow);
          border-color: var(--accent-color);
        }
        .copy-btn {
          background: var(--accent-gradient);
          border: none;
          color: var(--copy-btn-text);
          font-weight: 500;
          padding: 0.6rem 1.25rem;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.3s ease;
        }
        .copy-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px var(--accent-glow);
        }

        /* ---------- responsive ---------- */
        @media (max-width: 768px) {
          .stats-grid { grid-template-columns: 1fr 1fr; }
          .plan-list  { grid-template-columns: 1fr; }
          .dash-actions { display: flex; gap: 0.5rem; width: 100%; }
          .dash-actions .dash-btn { flex: 1; margin-left: 0; text-align: center; }
        }
        @media (max-width: 480px) {
          .stats-grid { grid-template-columns: 1fr; }
          .dash-root  { padding: 1rem; }
        }
      `}</style>

      {/* Header */}
      <div className="dash-header">
        <div className="dash-greeting">
          <h5>Welcome back, {user?.name || 'Trader'}!</h5>
        </div>
        <div className="dash-actions">
          <Link to="/invest" className="dash-btn dash-btn-success">
            Deposit
          </Link>
          <Link to="/withdrawal" className="dash-btn dash-btn-danger">
            Withdraw
          </Link>
        </div>
      </div>

      {/* Welcome alert */}
      <div className="dash-alert">
        Welcome to your trading dashboard — track your balance, profits and
        plans at a glance.
      </div>

      {/* Stats grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-wallet"></i></div>
          <h4 className="stat-value">${fmt(accountBalance())}</h4>
          <p className="stat-label">Account Balance</p>
        </div>

        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-chart-line"></i></div>
          <h4 className="stat-value">${fmt(currentEarning())}</h4>
          <p className="stat-label">Total Profit</p>
        </div>

        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-gift"></i></div>
          <h4 className="stat-value">${fmt(bonusBalance())}</h4>
          <p className="stat-label">Bonus</p>
        </div>

        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-users"></i></div>
          <h4 className="stat-value">${fmt(referralBalance())}</h4>
          <p className="stat-label">Referral Bonus</p>
        </div>

        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-arrow-down"></i></div>
          <h4 className="stat-value">${fmt(totalDeposit)}</h4>
          <p className="stat-label">Total Deposit</p>
        </div>

        <div className="stat-card">
          <div className="stat-icon"><i className="fas fa-arrow-up"></i></div>
          <h4 className="stat-value">${fmt(totalWithdrawal)}</h4>
          <p className="stat-label">Total Withdrawal</p>
        </div>
      </div>

      {/* Active Plans */}
      <div className="section-card">
        <h5 className="section-title">
          <i className="fas fa-rocket"></i> Active Plan(s)
        </h5>

        {activePlans.length === 0 ? (
          <div className="empty-state">
            <p>You do not have an active investment plan at the moment.</p>
            <Link to="/invest" className="dash-btn">
              Explore Investment Plans
            </Link>
          </div>
        ) : (
          <div className="plan-list">
            {activePlans.map((inv) => (
              <Link to="/investment" key={inv.id} className="plan-item">
                <div className="plan-info">
                  <h6>{inv.plan?.name || 'Investment Plan'}</h6>
                  <p>${fmt(inv.amount)} &middot; {inv.status || 'approved'}</p>
                </div>
                <div className="plan-arrow"><i className="fas fa-arrow-right"></i></div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Live crypto prices */}
      <CryptoList />

      {/* Recent Transactions */}
      <div className="section-card">
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
            flexWrap: 'wrap',
            gap: '0.5rem',
          }}
        >
          <h5 className="section-title" style={{ marginBottom: 0 }}>
            <i className="fas fa-exchange-alt"></i> Recent Transactions
            <span
              style={{
                marginLeft: '0.5rem',
                background: 'var(--glass-effect)',
                color: 'var(--text-secondary)',
                padding: '0.15rem 0.5rem',
                borderRadius: '6px',
                fontSize: '0.85rem',
              }}
            >
              {recentTransactions.length}
            </span>
          </h5>
          <Link
            to="/dashboard"
            style={{
              background: 'var(--glass-effect)',
              color: 'var(--accent-color)',
              padding: '0.4rem 0.8rem',
              borderRadius: '8px',
              fontSize: '0.85rem',
              textDecoration: 'none',
            }}
          >
            <i className="fas fa-clipboard"></i> View all
          </Link>
        </div>

        <div className="tx-wrap">
          <table className="tx-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {recentTransactions.length === 0 ? (
                <tr>
                  <td
                    colSpan="4"
                    style={{
                      textAlign: 'center',
                      padding: '2rem 1rem',
                      color: 'var(--text-secondary)',
                    }}
                  >
                    No transactions yet
                  </td>
                </tr>
              ) : (
                recentTransactions.map((tx) => (
                  <tr key={tx.id}>
                    <td>{formatDate(tx.date)}</td>
                    <td>{tx.type}</td>
                    <td>${fmt(tx.amount)}</td>
                    <td>
                      <span className={`tx-status ${(tx.status || '').toLowerCase()}`}>
                        {tx.status || 'pending'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Referral */}
      <div className="referral-card">
        <h5 className="referral-title">
          <i className="fas fa-user-plus" style={{ marginRight: '0.5rem' }}></i>
          Refer Us &amp; Earn
        </h5>
        <p style={{ color: 'var(--text-secondary)', marginBottom: 0 }}>
          Use the link below to invite your friends and earn rewards.
        </p>
        <div className="referral-input-row">
          <input
            type="text"
            className="referral-input"
            value={referralLink}
            readOnly
          />
          <button className="copy-btn" onClick={copyReferral} type="button">
            <i className="fas fa-copy" style={{ marginRight: '0.4rem' }}></i>
            Copy
          </button>
        </div>
      </div>
    </div>
  );
};

export default Index;